<!-- Your app's shell. THIS FILE IS YOURS — the factory never replaces it.
     Everything imported from ./core is maintained by `appfactory upgrade`.

     This file is now DELIBERATELY THIN. It is the root: an IonApp, the outlet
     the router paints into, and the handful of things that must exist on every
     screen at once — the demo banner, onboarding, toasts, live regions.

     The screens themselves live in src/pages/, wired up in src/router.js, and
     the state they share is src/store.js. That split is the whole reason the
     chassis moved to Ionic's router: pages that mount and unmount get real
     push/pop transitions and a per-tab back stack, which a single component
     switching on a `current` string cannot have.

     WHERE TO START:
       src/router.js      your tabs, and what each one opens
       src/pages/         one file per screen — replace the worked example
       src/store.js       the data those screens read and write
       src/styles/app.css your own CSS, loaded last and never overwritten

     The worked example runs as-is. REPLACE THE CONTENT AND KEEP THE DENSITY:
     whatever is in pages/ is what the next app looks like. -->
<template>
  <IonApp>
    <!-- Says, permanently, that this is not your data.
         Outside the router outlet on purpose, so it shows on every screen
         rather than only the one that happened to render it. The chassis used
         to put this inside its own AppShell — the Ionic port dropped that
         component, the banner silently stopped rendering, and the demo no
         longer said it was a demo. That is the one thing core/demo.js insists
         on: sample data that looks like your own is a support ticket. -->
    <!-- The strips that are true of the whole app, in one wrapper.
         ONE FLEX ITEM, and that is the whole reason the wrapper exists:
         `ion-app` is a flex column with `justify-content: space-between`, so
         two in-flow children put the first at the top of the screen and the
         SECOND AT THE BOTTOM, over the tab bar. One child cannot be
         space-between-ed anywhere.

         The simulation strip goes above the demo banner so that the topmost
         strip is the one carrying the safe area, and because "this did not
         happen" outranks "this is not your data". In /demo both are true and
         both are up. -->
    <div class="la-banners">
      <SimulationBanner v-if="simulating" />
      <DemoBanner v-if="isDemo" />
    </div>

    <!-- The class, not a variable, is what moves the outlet down: the demo
         banner's own rule sets `top` from one custom property, and a class
         beats it on specificity in both directions — which is the only way a
         second strip can be added without editing a factory-owned file. -->
    <IonRouterOutlet :class="{ 'la-simulating': simulating }" />

    <!-- The train through the crossing while the saved rows are read back.
         Fixed over everything, gone the moment boot is done and the first
         crossing has been through. -->
    <!-- AND WHILE A LINK IS STILL SETTLING. A shared disruption has to wait
         for the first feed before it can know whether the notice is still
         running (see src/share-open.js), and the honest thing to show in that
         second is the screen this app already shows while it is reading
         — not the departures board, which would then jump somewhere else and
         read as a misfire. On a cold start the splash is up regardless and
         this only stops it leaving early. -->
    <Transition name="splash">
      <LoadingScreen v-if="booting || shareSettling" />
    </Transition>

    <!-- Three or four cards, purpose and how-to-use. Shown once, ever.
         Give it an empty array and nothing renders at all. -->
    <Onboarding :cards="onboarding" />

    <!-- Toasts, the undo bar, and the screen-reader live regions.
         Mounted once, here. Nothing imports it to use it. -->
    <Overlays />

    <!-- The subscription wall. Opened only by a gate (Alerts ON, Follow, the
         test alert) or once after a lapse — see src/billing.js. Never on
         launch. -->
    <Paywall />
    <TermsNotice />
  </IonApp>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { bus } from './core/state.js';
import { Onboarding, DemoBanner, Overlays, isDemo } from './core/ui';
import LoadingScreen from './components/LoadingScreen.vue';
import SimulationBanner from './components/SimulationBanner.vue';
import Paywall from './components/Paywall.vue';
import TermsNotice from './components/TermsNotice.vue';
import { simulating } from './app-state.js';
import { storeReady } from './store.js';
import { applyShare, offerShare, sharePending, shareSettling } from './share-open.js';
// Through `prefs` rather than localStorage directly, and that is the whole of
// why this import exists: `prefs` is memory under /demo (see
// src/core/storage.js), and this was the one thing in the app that wrote to
// the real profile from a demo — a stamp that then made the REAL app skip its
// splash for three minutes because somebody had looked at /demo.
import { prefs } from './core/storage.js';

// Boot is the store reading back the saved rows; the floor is the first
// level crossing going through. Whichever is later ends the splash, and a
// store that fails to read must not leave it up: the app behind it works
// without saved rows and says so itself.
//
// SHORT. The scene is worth seeing and it is not worth waiting for: somebody
// opening this app is usually standing on a platform wanting to know whether
// their train is coming, and every second of animation is a second of not
// answering. So the floor is the shortest interval that does not read as a
// flash of something going wrong, and the screen is gone as soon as the store
// has read back. It leaves on a fade rather than a cut, which is what lets it
// go part-way through a pass without the train appearing to vanish.
//
// ONCE per launch. An over-the-air update that arrives at boot reloads the
// WebView into the new bundle a second or two after the first paint, and the
// splash ran again on the other side of it — train, board, train. So the
// moment the splash was shown is kept on the device, and a boot that follows
// one within three minutes skips it: that is a reload, not an opening.
// It used to hold for a whole pass of the scene, never less than 3.28 s, so
// that the first train got through the crossing and off the far side before
// the splash was taken away. That is the right rule for a screen somebody is
// meant to watch and the wrong one for a screen in front of an answer. The
// fade does the work the whole pass used to: a train a third of the way
// across dissolves, rather than being cut off.
const SPLASH_FLOOR_MS = 900;
const SPLASH_ONCE_MS = 3 * 60_000;
const SPLASH_KEY = 'lateagain:splashed-at';
const shownRecently = Date.now() - Number(prefs[SPLASH_KEY] || 0) < SPLASH_ONCE_MS;
const booting = ref(!shownRecently);
if (!shownRecently) {
  prefs[SPLASH_KEY] = Date.now();
  Promise.allSettled([storeReady, new Promise((r) => setTimeout(r, SPLASH_FLOOR_MS))])
    .then(() => { booting.value = false; });
}

// Navigation asked for from outside the component tree.
//
// `src/app-state.js` cannot import the router — it is imported by every page
// and by the test suite, and pulling `src/router.js` in would drag the whole
// page graph into the first chunk — so the one place that needs to navigate
// without a component, the "See options" button on a pushed alert, announces
// where it wants to go and this shell takes it there. The shell is the only
// component guaranteed to be mounted when a notification arrives.
const router = useRouter();
let offNavigate = null;
let offRoute = null;
let offOnboarding = null;
let offScreen = null;
onMounted(() => {
  offNavigate = bus.on('navigate', (to) => {
    const path = String(to ?? '');
    if (!path.startsWith('/')) return;
    router.push(path).catch(() => { /* already there, or no such route */ });
  });

  // A SHARED LINK, from either direction.
  //
  // The cold start has already been read — `src/share-open.js` takes it off
  // `location` at module load, because main.js emits its `route` before this
  // component mounts and a listener here would miss it. This listener is for
  // the OTHER case: a native deep link arriving at an app that is already
  // running, which reaches the bus through `app.route(url)`.
  offRoute = bus.on('route', (url) => {
    if (!offerShare(url)) return;
    // THE SAME GATE AS THE WATCH BELOW, and it has to be here too.
    //
    // `applyShare` takes the descriptor before it awaits anything, so calling
    // it during the welcome CONSUMES the link and then loses it: the route it
    // pushes is bounced straight back by the onboarding guard, and the watch
    // that would have applied it later finds nothing pending. The identical
    // link opened in a browser survives, because the cold-start path only ever
    // calls `offerShare` — so without this a shared link behaves differently
    // in the app than on the web, which is the one thing it must never do.
    const name = router.currentRoute.value?.name;
    if (name === 'onboarding' || !name) return;
    applyShare(router);
  });

  // ONBOARDING FIRST, ALWAYS. A link that pulled somebody out of the welcome
  // would lose the four answers it is asking for, so the descriptor waits and
  // is applied the moment the welcome is behind them. On a phone that has
  // already been set up this fires on the first tick and there is no welcome
  // to wait for.
  offOnboarding = watch(
    () => router.currentRoute.value?.name,
    (name) => {
      if (!sharePending.value || name === 'onboarding' || !name) return;
      applyShare(router);
    },
    { immediate: true },
  );

  // WHICH SCREEN IS ON, as an attribute on the root element, for CSS.
  //
  // One rule needs it and it is the map: every other screen in this app is a
  // ledger and shares one measured column (see `--la-app-max` in
  // src/styles/app.css), and the map is a drawing that is straightforwardly
  // better with more glass. There is nothing in the DOM to hang that on —
  // Ionic gives every page the same `ion-content.page`, and `:scroll-y="false"`,
  // which is the one honest difference, is set as a PROPERTY and never appears
  // as an attribute a selector could read.
  //
  // So the route says it, because the route is what actually knows. Written
  // here rather than in MapPage.vue on purpose: the rule is about the shell's
  // width, the shell owns it, and a page should not be reaching out to resize
  // the app around itself.
  offScreen = watch(
    () => router.currentRoute.value?.name,
    (name) => { document.documentElement.dataset.screen = String(name ?? ''); },
    { immediate: true },
  );
});
onUnmounted(() => { offNavigate?.(); offRoute?.(); offOnboarding?.(); offScreen?.(); });

// NO ONBOARDING CARDS, on purpose.
//
// The chassis ships three — welcome, offline, self-updating — and they are
// good defaults for an app whose first screen is a blank list and a form.
// This app's first screen is one sentence and one button: "No lines yet. Add
// the ones you actually catch." A three-card carousel in front of that is a
// dialog asking you to dismiss it before you can read the invitation, and it
// covers the only screen anyone judges the app by.
//
// An empty array renders nothing at all. Put cards back here if the empty
// state ever stops carrying it.
const onboarding = [];
</script>

<!-- Not scoped: the rule has to reach ion-router-outlet, which is not this
     component's element. Both properties are unset when the strip in question
     is not up, so the sum is 0 in the ordinary app. -->
<style>
.splash-leave-active { transition: opacity 240ms ease; }
.splash-leave-to { opacity: 0; }

/* Above the outlet, which is absolutely positioned over the whole app. */
.la-banners {
  position: relative;
  z-index: 3;
}

ion-app > ion-router-outlet.la-simulating {
  top: calc(var(--demo-banner-h, 0px) + var(--la-sim-banner-h, 0px));
}
</style>

